import {
  TimelineViewModelData,
  BuildItemsReducerState,
  ViewModelSourceData,
  RelevantCompontsScopeDataMap,
  WorkspaceAndFieldsRegistry,
} from '../types';
import { fieldToRegistryItem } from './calculations';
import { APIComponentType, UnitOfTime } from '@ardoq/api-types';
import { MAX_DATE, MIN_DATE } from '@ardoq/date-range';
import { buidItemsReducer } from './utils';
import { areDatesEffectivelyEqual } from '../calculations';
import { add, startOfToday, sub } from '@ardoq/date-time';
import {
  ComponentTypeForLegend,
  componentTypeToLegendItem,
} from '@ardoq/view-legend';
import { isTruthy } from '@ardoq/common-helpers';

const buildComponentTypeLookup = (
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry
): Map<string, APIComponentType> =>
  new Map<string, APIComponentType>(
    Array.from(workspaceAndFieldsRegistry.values()).flatMap(workspace =>
      workspace.allComponentTypesInWorkspace.map(
        componentType => [componentType.id, componentType] as const
      )
    )
  );

const getComponentTypesForLegend = (
  componentIds: string[],
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap,
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry
): ComponentTypeForLegend[] => {
  const typeIdToType = buildComponentTypeLookup(workspaceAndFieldsRegistry);
  const uniqueTypeIds = new Set(
    componentIds.map(
      componentId =>
        viewRelevantComponentsScopeData.get(componentId)?.componentTypeId
    )
  );
  return [...uniqueTypeIds]
    .map(typeId => typeId && typeIdToType.get(typeId))
    .filter(isTruthy)
    .map(componentTypeToLegendItem);
};

type BuildViewModelData = (args: ViewModelSourceData) => TimelineViewModelData;

export const buildViewModelData: BuildViewModelData = ({
  contextComponentId,
  hasGroupingStyles: hasGrouping,
  viewRelevantComponentsScopeData,
  workspaceAndFieldsRegistry,
  fieldsAndItemsSourceData,
  viewInterface: {
    features: { isModernizedStyle: isFieldDataCollapsingEnabled },
  },
}) => {
  const {
    viewGraphHierarchy,
    startSetNodes,
    orderedSelectedDateRangeFields,
    orderedSelectedMilestoneFields,
    relevantDateRangeFieldsWithWorkspaceNames,
    relevantMilestoneFieldsWithWorkspaceNames,
  } = fieldsAndItemsSourceData;

  // build the filtering result to a view model
  const initialBuildItemsReducerState: BuildItemsReducerState = {
    items: [],
    chartDomain: [MAX_DATE, MIN_DATE],
    displayedComponentIds: [],
    collapsibleGroupIds: new Set<string>(),
    rootCollapsibleGroupIds: new Set<string>(),
    collapsibleFieldDataIds: new Set<string>(),
    rootCollapsibleFieldDataIds: new Set<string>(),
    nodeToItemParameters: {
      viewGraphHierarchy,
      orderedSelectedDateRangeFields,
      orderedSelectedMilestoneFields,
      contextComponentId,
      viewRelevantComponentsScopeData,
      isFieldDataCollapsingEnabled,
      workspaceAndFieldsRegistry,
    },
  };
  const builtItemsData = startSetNodes.reduce(
    buidItemsReducer(),
    initialBuildItemsReducerState
  );

  // set the chart domain to a default value if there are no dates in the dataset
  const areNoDatesInDataset =
    areDatesEffectivelyEqual(builtItemsData.chartDomain[0], MAX_DATE) &&
    areDatesEffectivelyEqual(builtItemsData.chartDomain[1], MIN_DATE);

  return {
    timelineViewFieldsRegistry: {
      // all relevant timeSpanFields & milestoneFields are needed for composing
      // the empty state and settings bar menus
      timeSpanFields:
        relevantDateRangeFieldsWithWorkspaceNames.map(fieldToRegistryItem),
      milestoneFields:
        relevantMilestoneFieldsWithWorkspaceNames.map(fieldToRegistryItem),
      // ordered arrays needed for legend and menus
      orderedSelectedPhaseFields:
        orderedSelectedDateRangeFields.map(fieldToRegistryItem),
      orderedSelectedMilestoneFields:
        orderedSelectedMilestoneFields.map(fieldToRegistryItem),
    },

    // defines the hierarchy of the view graph
    filteredViewGraphHierarchy: viewGraphHierarchy,

    // view props
    hasGrouping,
    viewModelData: builtItemsData.items,
    viewModelDataDomain: areNoDatesInDataset
      ? [
          sub(startOfToday(), { [UnitOfTime.DAY]: 1 }),
          add(startOfToday(), { [UnitOfTime.DAY]: 7 }),
        ]
      : builtItemsData.chartDomain,
    legendComponentTypes: getComponentTypesForLegend(
      builtItemsData.displayedComponentIds,
      viewRelevantComponentsScopeData,
      workspaceAndFieldsRegistry
    ),
    collapsibleGroups: {
      collapsible: builtItemsData.collapsibleGroupIds,
      rootCollapsible: Array.from(builtItemsData.rootCollapsibleGroupIds),
    },
    collapsibleFieldDataIds: {
      collapsible: builtItemsData.collapsibleFieldDataIds,
      rootCollapsible: Array.from(builtItemsData.rootCollapsibleFieldDataIds),
    },
  };
};
